import { apiRequest } from '../../api/client';

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

/** Sáu quyền của chủ thể dữ liệu (Điều 4 LBVDLCN 2025). */
export type DataSubjectRequestType =
  | 'Access'
  | 'Rectification'
  | 'Erasure'
  | 'RestrictProcessing'
  | 'WithdrawConsent';

export type DataSubjectRequestState = 'Pending' | 'InProgress' | 'Completed' | 'Rejected';

export interface DataSubjectRequest {
  id: string;
  memberId: string;
  memberCode: string;
  memberName: string | null;
  type: DataSubjectRequestType;
  state: DataSubjectRequestState;
  requestedAt: string;
  /** Hạn luật định. Server tính sẵn — client KHÔNG tự tính lại. */
  dueAt: string;
  completedAt: string | null;
  isOverdue: boolean;
  note: string | null;
  /**
   * Hệ thống đã làm xong lúc khách bấm (`handledByStaffId` rỗng).
   * Rút đồng ý / xem / sửa đều như vậy — lọc `Pending` sẽ giấu chúng.
   */
  isAutoCompleted: boolean;
}

export const privacyKeys = {
  requests: (state?: DataSubjectRequestState) => ['privacy-requests', state ?? 'all'] as const,
};

export const privacyApi = {
  list: (state?: DataSubjectRequestState) =>
    unwrap(
      apiRequest<DataSubjectRequest[]>(
        `/admin/privacy/requests${state ? `?state=${state}` : ''}`,
      ),
    ),

  complete: (id: string, note?: string) =>
    unwrap(
      apiRequest<DataSubjectRequest>(`/admin/privacy/requests/${id}/complete`, {
        method: 'POST',
        body: { note: note ?? null },
      }),
    ),

  reject: (id: string, reason: string) =>
    unwrap(
      apiRequest<DataSubjectRequest>(`/admin/privacy/requests/${id}/reject`, {
        method: 'POST',
        body: { reason },
      }),
    ),
};

/**
 * Nhãn tiếng Việt cho từng loại yêu cầu, kèm mức nghiêm trọng.
 *
 * `Erasure` là loại duy nhất **không lùi được** — nó ẩn danh hồ sơ thật (BR-804). Tách
 * ra bằng `isIrreversible` chứ không dựa vào người dùng đọc nhãn rồi tự cẩn thận.
 */
export const REQUEST_TYPES: Record<
  DataSubjectRequestType,
  { label: string; description: string; isIrreversible: boolean }
> = {
  Access: {
    label: 'Xem dữ liệu',
    description: 'Khách yêu cầu xem dữ liệu đang giữ. Hệ thống thường tự trả ngay.',
    isIrreversible: false,
  },
  Rectification: {
    label: 'Sửa dữ liệu',
    description: 'Khách sửa thông tin sai. Hệ thống tự làm khi khách bấm trong ứng dụng.',
    isIrreversible: false,
  },
  Erasure: {
    label: 'Xóa tài khoản',
    description:
      'Ẩn danh hồ sơ, giữ lại sổ cái điểm vì đó là chứng từ tài chính bắt buộc lưu. KHÔNG lùi được.',
    isIrreversible: true,
  },
  RestrictProcessing: {
    label: 'Hạn chế xử lý',
    description: 'Giữ dữ liệu nhưng ngừng dùng để tích điểm và gửi ưu đãi.',
    isIrreversible: false,
  },
  WithdrawConsent: {
    label: 'Rút lại đồng ý',
    description: 'Hệ thống thực hiện ngay khi khách bấm. Dòng này để chứng minh đã xử lý đúng hạn.',
    isIrreversible: false,
  },
};

export const REQUEST_STATES: Record<DataSubjectRequestState, string> = {
  Pending: 'Chờ xử lý',
  InProgress: 'Đang xử lý',
  Completed: 'Đã xong',
  Rejected: 'Đã từ chối',
};

/**
 * Còn bao lâu tới hạn, diễn đạt bằng lời.
 *
 * Hiện ngày ISO thì người trực phải tự trừ trong đầu — mà đó đúng là chỗ trễ hạn xảy ra.
 */
export function describeDeadline(dueAt: string, isOverdue: boolean): string {
  const remainingMs = new Date(dueAt).getTime() - Date.now();
  const days = Math.floor(Math.abs(remainingMs) / 86_400_000);
  const hours = Math.floor((Math.abs(remainingMs) % 86_400_000) / 3_600_000);

  if (isOverdue) {
    return days > 0 ? `Quá hạn ${days} ngày` : `Quá hạn ${hours} giờ`;
  }

  if (days > 0) return `Còn ${days} ngày`;
  return hours > 0 ? `Còn ${hours} giờ` : 'Còn dưới 1 giờ';
}

const URGENT_AUTO_TYPES: ReadonlySet<DataSubjectRequestType> = new Set([
  'WithdrawConsent',
  'RestrictProcessing',
]);

export function isOpenRequest(request: DataSubjectRequest): boolean {
  return request.state === 'Pending' || request.state === 'InProgress';
}

/**
 * Rút đồng ý / hạn chế xử lý hệ thống đã làm xong, vẫn trong hạn luật định.
 *
 * Ghim đầu danh sách "Tất cả" kèm badge "Vừa tự xử lý" — không đổi thành phiếu chờ
 * Super Admin. Để Pending là tiếp tục xử lý dữ liệu sau khi khách đã rút.
 */
export function isPinnedAutoCompleted(
  request: DataSubjectRequest,
  now = Date.now(),
): boolean {
  if (!request.isAutoCompleted) return false;
  if (!URGENT_AUTO_TYPES.has(request.type)) return false;
  return new Date(request.dueAt).getTime() > now;
}

export function sortPrivacyQueue(
  requests: DataSubjectRequest[],
  now = Date.now(),
): DataSubjectRequest[] {
  return [...requests].sort((a, b) => {
    const aPin = isPinnedAutoCompleted(a, now);
    const bPin = isPinnedAutoCompleted(b, now);
    if (aPin !== bPin) return aPin ? -1 : 1;

    if (aPin && bPin) {
      return completedAtMs(b) - completedAtMs(a);
    }

    const aOpen = isOpenRequest(a);
    const bOpen = isOpenRequest(b);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;

    if (aOpen && bOpen) {
      return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    }

    return completedAtMs(b) - completedAtMs(a);
  });
}

function completedAtMs(request: DataSubjectRequest): number {
  return request.completedAt ? new Date(request.completedAt).getTime() : 0;
}
